import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Scale, 
  Users, 
  Shield, 
  Award,
  FileCheck,
  Handshake,
  GraduationCap,
  TrendingUp,
  ArrowRight
} from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="section-container">
          {/* Page Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              About Blue Lion Claims
            </h1>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-6xl mx-auto">
            {/* Left Column - Main Text */}
            <div className="space-y-8">
              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-foreground/80 leading-relaxed">
                  As a legal group, our firms have supported more than <strong className="text-foreground">800,000 clients</strong> over 
                  the past 15 years across a wide range of consumer and financial claims. Over that period, we have helped 
                  recover in excess of <strong className="text-foreground">£100 million</strong> for clients through complaints, 
                  negotiated settlements and formal legal action where appropriate.
                </p>
                <p className="text-foreground/80 leading-relaxed">
                  We are regulated by the <strong className="text-foreground">Solicitors Regulation Authority</strong> and operate 
                  within strict professional and ethical standards. This means your claim is handled within a regulated legal 
                  framework, with proper duties of care, confidentiality and professional accountability.
                </p>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-border/60 bg-muted/30">
                  <CardContent className="p-5 text-center">
                    <Users className="w-8 h-8 text-accent mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">800,000+</p>
                    <p className="text-sm text-muted-foreground">Clients Supported</p>
                  </CardContent>
                </Card>
                <Card className="border-border/60 bg-muted/30">
                  <CardContent className="p-5 text-center">
                    <TrendingUp className="w-8 h-8 text-gold mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">£100m+</p>
                    <p className="text-sm text-muted-foreground">Recovered for Clients</p>
                  </CardContent>
                </Card>
                <Card className="border-border/60 bg-muted/30">
                  <CardContent className="p-5 text-center">
                    <Award className="w-8 h-8 text-accent mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">15+</p>
                    <p className="text-sm text-muted-foreground">Years Experience</p>
                  </CardContent>
                </Card>
                <Card className="border-border/60 bg-muted/30">
                  <CardContent className="p-5 text-center">
                    <Shield className="w-8 h-8 text-gold mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground">SRA</p>
                    <p className="text-sm text-muted-foreground">Regulated</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column - Why Law Firm */}
            <div className="space-y-8">
              <Card className="border-border/60 shadow-lg overflow-hidden">
                <div className="bg-primary p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center">
                      <Scale className="w-6 h-6 text-gold" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-primary-foreground">
                      Why Working With a Law Firm Matters
                    </h2>
                  </div>
                </div>
                <CardContent className="p-6 space-y-6">
                  <p className="text-foreground/80 leading-relaxed">
                    Many people choose to work with a law firm rather than a claims management company because 
                    law firms are trained in the legal process from end to end. We understand how claims are 
                    assessed, how compensation is calculated, and how lenders and insurers evaluate liability 
                    and settlement value.
                  </p>
                  <p className="text-foreground/80 leading-relaxed">
                    This understanding of quantum and legal positioning helps ensure that claims are properly 
                    framed, supported with evidence, and progressed through the appropriate legal channels.
                  </p>
                  
                  {/* Key Benefits */}
                  <div className="space-y-4 pt-4 border-t border-border">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Legal Expertise</h4>
                        <p className="text-sm text-muted-foreground">
                          Trained in dispute resolution and litigation strategy
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <FileCheck className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Formal Legal Remedies</h4>
                        <p className="text-sm text-muted-foreground">
                          Ability to escalate beyond informal complaints processes
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Handshake className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Acting in Your Best Interests</h4>
                        <p className="text-sm text-muted-foreground">
                          Professional duty of care and confidentiality
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Our Role */}
              <div className="p-6 rounded-xl bg-muted/50 border border-border">
                <h3 className="font-heading font-semibold text-foreground mb-3">
                  Our Commitment to You
                </h3>
                <p className="text-foreground/80 leading-relaxed text-sm">
                  Our role is to act in your best interests, guide you through what can be a complex process, 
                  and ensure your claim is presented clearly, accurately and fairly. We deal with the legal 
                  and administrative burden so that you do not have to, while keeping you informed at each 
                  stage of the process.
                </p>
              </div>

              {/* CTA */}
              <div className="text-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                    Find Your Agreements
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
